// Sprint CRM-3 — collegamenti dell'opportunità con il gestionale (preventivi, pratica, anagrafica, veicolo caricato)
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";
import { NuovoPreventivoModal } from "@/components/PreventiviTab";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { errore } from "./crmComuni";

const STATO = { draft: "Bozza", in_corso: "In corso", confermato: "Confermato", scaduto: "Scaduto", annullato: "Annullato",
  impegnata: "Impegnata", venduta: "Venduta", consegnata: "Consegnata", annullata: "Annullata" };

function Cerca({ titolo, carica, onScegli, onChiudi, prefix }) {
  const [q, setQ] = useState("");
  const [lista, setLista] = useState([]);
  useEffect(() => { const t = setTimeout(() => carica(q).then(setLista).catch(() => setLista([])), 250); return () => clearTimeout(t); }, [q, carica]);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent data-testid={`${prefix}-dialog`}>
        <DialogHeader><DialogTitle>{titolo}</DialogTitle></DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per codice" data-testid={`${prefix}-cerca`} />
        <div className="max-h-72 overflow-y-auto divide-y">
          {lista.map((x) => (
            <button key={x.id} className="block w-full px-2 py-2 text-left text-sm hover:bg-slate-50" onClick={() => onScegli(x)}
              data-testid={`${prefix}-voce-${x.id}`}>{x.etichetta}</button>))}
          {!lista.length && <div className="p-2 text-sm text-slate-500" data-testid={`${prefix}-vuoto`}>Nessun risultato</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CollegamentiOpportunita({ o, onCambiata }) {
  const confirm = useConfirm();
  const [d, setD] = useState(null);
  const [dialog, setDialog] = useState(null);  // «preventivo» | «vendita» | «veicolo»
  const carica = useCallback(() => api.get(`/crm/opportunita/${o.id}/collegamenti`).then((r) => setD(r.data)).catch(() => setD(null)), [o.id]);
  useEffect(() => { carica(); }, [carica, o.updated_at]);
  if (!d || d.tipo_pipeline === "autonoma") return null;
  const fatto = (r) => { setDialog(null); onCambiata?.(r?.data); carica(); };

  const collega = async (v, sostituisci = false) => {
    try { fatto(await api.post(`/crm/opportunita/${o.id}/vendite`, { vendita_id: v.id, sostituisci_veicolo: sostituisci })); }
    catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.richiede_conferma) {
        const ok = await confirm({ title: "Veicolo diverso", message: `${det.messaggio} (${det.veicolo?.codice || ""} ${det.veicolo?.marca || ""} ${det.veicolo?.modello || ""}). Il precedente resta nello storico.`, confirmLabel: "Sostituisci" });
        if (ok) collega(v, true);
      } else toast.error(errore(e));
    }
  };
  const scollega = async (v) => {
    if (!(await confirm({ title: "Scollega", message: `Scollegare ${v.codice || "la vendita"} dall'opportunità?`, confirmLabel: "Scollega" }))) return;
    try { fatto(await api.delete(`/crm/opportunita/${o.id}/vendite/${v.id}`)); } catch (e) { toast.error(errore(e)); }
  };
  const cercaVendite = (q) => api.get(`/crm/opportunita/${o.id}/vendite-collegabili`, { params: { q } })
    .then((r) => r.data.map((v) => ({ ...v, etichetta: `${v.codice} · ${v.tipo} · ${STATO[v.status] || v.status || "—"}` })));
  const cercaVeicoli = (q) => api.get("/crm/veicoli-agganciabili", { params: { company_id: o.company_id, q } })
    .then((r) => r.data.map((v) => ({ ...v, etichetta: `${v.codice} · ${v.marca || ""} ${v.modello || ""} · ${v.targa || "—"}` })));
  const collegaVeicolo = async (v) => {
    try { fatto(await api.post(`/crm/opportunita/${o.id}/collega-veicolo`, { vehicle_id: v.id })); } catch (e) { toast.error(errore(e)); }
  };

  return (
    <Card className="p-4 space-y-2" data-testid="crm-opp-collegamenti">
      <div className="font-semibold">Collegamenti</div>
      {d.vendite.map((v) => (
        <div key={v.id} className="flex items-center justify-between text-sm" data-testid={`crm-opp-vendita-${v.id}`}>
          {v.visibile ? <Link className="text-sky-700 hover:underline" to={`/vendite/${v.id}`}>{v.tipo === "pratica" ? "Pratica" : "Preventivo"} {v.codice} · {STATO[v.status] || v.status}</Link>
            : <span className="text-slate-500">Vendita non visibile con i tuoi permessi</span>}
          <Button size="sm" variant="ghost" onClick={() => scollega(v)} data-testid={`crm-opp-scollega-${v.id}`}>Scollega</Button>
        </div>))}
      {d.anagrafica && <div className="text-sm" data-testid="crm-opp-anagrafica">Anagrafica: <Link className="text-sky-700 hover:underline" to={`/anagrafiche/${d.anagrafica.id}`}>{d.anagrafica.codice} · {d.anagrafica.nome}</Link></div>}
      {d.veicolo?.visibile && <div className="text-sm" data-testid="crm-opp-veicolo-collegato">Veicolo: <Link className="text-sky-700 hover:underline" to={`/vehicles/${d.veicolo.id}`}>{d.veicolo.codice} · {d.veicolo.marca} {d.veicolo.modello}</Link></div>}
      <div className="flex flex-wrap gap-2 pt-1">
        {d.tipo_pipeline === "vendita" && (d.serve_veicolo
          ? <span className="text-sm text-slate-500" data-testid="crm-opp-serve-veicolo">Aggancia prima un veicolo dell'archivio</span>
          : d.puo_creare_preventivo && <Button size="sm" onClick={() => setDialog("preventivo")} data-testid="crm-opp-crea-preventivo">Crea preventivo</Button>)}
        {d.tipo_pipeline === "vendita" && <Button size="sm" variant="outline" onClick={() => setDialog("vendita")} data-testid="crm-opp-collega-vendita">Collega preventivo o pratica</Button>}
        {d.collega_veicolo && <Button size="sm" variant="outline" onClick={() => setDialog("veicolo")} data-testid="crm-opp-collega-veicolo">Collega veicolo caricato</Button>}
      </div>
      {dialog === "preventivo" && <NuovoPreventivoModal open onOpenChange={(x) => { if (!x) setDialog(null); }} vehicleId={o.vehicle_id}
        vehicleCompanyId={o.company_id} crmOpportunitaId={o.id} onCreated={() => fatto()} />}
      {dialog === "vendita" && <Cerca titolo="Collega preventivo o pratica" carica={cercaVendite} onScegli={(v) => collega(v)} onChiudi={() => setDialog(null)} prefix="crm-cerca-vendita" />}
      {dialog === "veicolo" && <Cerca titolo="Collega veicolo caricato" carica={cercaVeicoli} onScegli={collegaVeicolo} onChiudi={() => setDialog(null)} prefix="crm-cerca-veicolo" />}
    </Card>
  );
}

// Riga «Opportunità: … · seguita da …» di preventivo e pratica (solo se l'utente la vede; altrimenti niente)
export function useOpportunitaVendita(venditaId) {
  const [opp, setOpp] = useState(null);
  useEffect(() => {
    if (!venditaId) return;
    api.get(`/crm/vendite/${venditaId}/opportunita`).then((r) => setOpp(r.data || null)).catch(() => setOpp(null));
  }, [venditaId]);
  return opp;
}

export function RigaOpportunita({ venditaId }) {
  const opp = useOpportunitaVendita(venditaId);
  if (!opp) return null;
  return (
    <div className="text-sm" data-testid="vendita-riga-opportunita">Opportunità: <Link className="text-sky-700 hover:underline" to={`/crm/opportunita/${opp.id}`}>{opp.descrizione}</Link> · seguita da {opp.titolare_nome || "—"}</div>
  );
}

// Dopo la creazione di un veicolo: proposta di collegamento alle acquisizioni con la stessa targa
export async function proponiAcquisizioni(confirm, proposte, vehicleId) {
  for (const p of proposte || []) {
    const ok = await confirm({ title: "Acquisizione CRM", message: `Questo veicolo corrisponde all'acquisizione «${p.descrizione}»: collegarla?`, confirmLabel: "Collega" });
    if (!ok) continue;
    try { await api.post(`/crm/opportunita/${p.id}/collega-veicolo`, { vehicle_id: vehicleId }); toast.success("Acquisizione collegata"); }
    catch (e) { toast.error(errore(e)); }
  }
}

// Finestra «Cliente» della vendita (VenditaDetail): voci dal contatto CRM, solo se la vendita è collegata a un'opportunità
const CAMPI_FORM = [["nome", "Nome"], ["cognome", "Cognome"], ["ragione_sociale", "Ragione sociale"], ["codice_fiscale", "Codice fiscale"],
  ["partita_iva", "Partita IVA"], ["telefono", "Telefono"], ["mobile", "Mobile"], ["email", "Email"], ["note", "Note"]];

export async function collegaContatto(opp, anagraficaId) {
  const kid = opp?.contatto?.id;
  if (!kid || opp.contatto.anagrafica_id) return null;
  const r = await api.post(`/crm/contatti/${kid}/anagrafica`, { anagrafica_id: anagraficaId });
  return { differenze: r.data.differenze || [], anagrafica: r.data.anagrafica };
}

export function DifferenzeAnagrafica({ esito, onFine }) {
  const [scelti, setScelti] = useState({});
  if (!esito?.differenze?.length) return null;
  const porta = async () => {
    const payload = {}; const originali = {};
    esito.differenze.filter((x) => scelti[x.campo]).forEach((x) => {
      if (x.campo.startsWith("indirizzo.")) { const k = x.campo.split(".")[1]; payload.indirizzo = { ...(payload.indirizzo || {}), [k]: x.contatto }; }
      else payload[x.campo] = x.contatto;
      originali[x.campo] = x.anagrafica ?? null;
    });
    try {
      if (Object.keys(payload).length) {
        const r = await api.patch(`/anagrafiche/${esito.anagrafica.id}`, { ...payload, originali });
        if (r.data?.conflitti?.length) toast.warning("Alcuni campi erano cambiati nel frattempo: tenuti i valori dell'anagrafica");
        else toast.success("Anagrafica aggiornata");
      }
    } catch (e) { toast.error(errore(e)); }
    onFine();
  };
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-sm space-y-1" data-testid="crm-differenze-anagrafica">
      <div className="font-medium">Contatto CRM e anagrafica sono diversi: scegli cosa portare nell'anagrafica (senza permesso restano quelli dell'anagrafica)</div>
      {esito.differenze.map((x) => (
        <label key={x.campo} className="flex items-center gap-2" data-testid={`crm-diff-${x.campo}`}>
          <input type="checkbox" checked={!!scelti[x.campo]} onChange={(e) => setScelti({ ...scelti, [x.campo]: e.target.checked })} />
          {x.campo}: <b>{x.contatto}</b> al posto di «{x.anagrafica || "—"}»</label>))}
      <div className="flex gap-2"><Button size="sm" onClick={porta} data-testid="crm-diff-porta">Porta i valori scelti</Button>
        <Button size="sm" variant="ghost" onClick={onFine} data-testid="crm-diff-lascia">Lascia quelli dell'anagrafica</Button></div>
    </div>
  );
}

export function ClienteDaCrm({ opp, onCreata }) {
  const [form, setForm] = useState(null);
  const [verifica, setVerifica] = useState(false);
  if (!opp?.contatto || opp.contatto.anagrafica_id) return null;
  const apri = async () => {
    try { const r = await api.get(`/crm/contatti/${opp.contatto.id}/proposta-anagrafica`); setForm(r.data.anagrafica); setVerifica(r.data.verifica_nome_cognome); }
    catch (e) { toast.error(errore(e)); }
  };
  const crea = async () => {
    try {
      const r = await api.post("/anagrafiche", form);
      const esito = await collegaContatto(opp, r.data.id);
      toast.success(`Anagrafica ${r.data.codice || ""} creata e collegata al contatto`);
      onCreata(r.data.id, esito);
      setForm(null);
    } catch (e) { toast.error(errore(e)); }
  };
  const privato = form?.tipo_persona === "privato";
  return (
    <div className="rounded border border-sky-200 bg-sky-50 p-2 text-sm space-y-2" data-testid="crm-cliente-da-contatto">
      <div>Opportunità CRM: contatto <b>{opp.contatto.nome}</b> senza anagrafica · seguita da {opp.titolare_nome || "—"}</div>
      {!form ? <div className="flex gap-2">
        <Button size="sm" onClick={apri} data-testid="crm-crea-anagrafica">Crea anagrafica dal contatto CRM</Button>
        <span className="self-center text-slate-600">oppure «Collega un'anagrafica esistente» scegliendola qui sotto</span></div>
        : <div className="grid grid-cols-2 gap-2">
          {CAMPI_FORM.filter(([k]) => (privato ? k !== "ragione_sociale" : !["nome", "cognome"].includes(k))).map(([k, l]) => (
            <div key={k} className={verifica && ["nome", "cognome"].includes(k) ? "rounded ring-2 ring-amber-400 p-1" : ""}>
              <div className="text-xs text-slate-500">{l}</div>
              <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`crm-anag-${k}`} /></div>))}
          {verifica && <div className="col-span-2 text-amber-800" data-testid="crm-anag-verifica">Verifica la divisione fra nome e cognome</div>}
          <div className="col-span-2 flex gap-2"><Button size="sm" onClick={crea} data-testid="crm-anag-crea">Crea e collega</Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(null)} data-testid="crm-anag-annulla">Annulla</Button></div>
        </div>}
    </div>
  );
}
